/**
 * GameOverScene — in-canvas game-over display.
 *
 * Holds the canvas-side game-over visuals (final score, restart button)
 * while the HTML overlay panel (in `src/ui/overlay.ts`) holds the email
 * prompt, leaderboard, and the primary PLAY AGAIN button. Both surfaces
 * stay in sync; whichever the user taps first wins.
 *
 * Why a separate scene instead of just an overlay: the overlay is
 * positioned in HTML and can be dismissed with the BACK button on
 * the leaderboard panel. If the user then expects a clear restart
 * path, we need a visible, tap-able button on the canvas itself. This
 * scene provides that — its RESTART button is 240×80 px and centred
 * above the "PRESS [R]" hint, so a phone user can always re-start.
 */
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../Game";
import { clearPlayerIdent, showInfoPrompt } from "../../ui/overlay";

interface GameOverData {
  score: number;
}

export class GameOverScene extends Phaser.Scene {
  private score = 0;
  private restartTriggered = false;

  constructor() {
    super({ key: "GameOverScene" });
  }

  public init(data: GameOverData): void {
    this.score = data.score ?? 0;
  }

  public create(): void {
    this.cameras.main.setBackgroundColor("#000000");
    const cx = GAME_WIDTH / 2;

    // Big "GAME OVER" header.
    this.add.text(cx, GAME_HEIGHT * 0.30, "GAME OVER", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "48px",
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 4,
    }).setOrigin(0.5);

    // Final score.
    this.add.text(cx, GAME_HEIGHT * 0.45, `FINAL SCORE  ${this.score}`, {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "20px",
      color: "#ffffff",
    }).setOrigin(0.5);

    // Large touch-restart button — sized for thumbs on a 540-tall phone,
    // and positioned in the lower-middle of the canvas so it's reachable
    // without overlapping the HUD. Clicking it dispatches traszka:restart
    // and immediately starts a fresh GameScene so there's no flicker.
    const restartBtn = this.add
      .text(cx, GAME_HEIGHT * 0.72, "PLAY AGAIN", {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: "32px",
        color: "#000000",
        backgroundColor: "#ffffff",
        padding: { x: 36, y: 16 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    // Give it a generous hit area so thumbs don't need pixel-perfect taps.
    const hitW = 280;
    const hitH = 100;
    restartBtn.on("pointerdown", () => this.triggerRestart());
    // Hand-drawn hit-area rectangle (Phaser style — setInteractive above
    // already covers the text bbox; we extend it visually via the padding).

    // Subtitle hint for keyboard / mouse.
    this.add.text(cx, GAME_HEIGHT * 0.86, "OR PRESS [R]", {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: "14px",
      color: "#888888",
    }).setOrigin(0.5);

    // Keyboard shortcut for desktop / plugged-in keyboards.
    this.input.keyboard?.addKey("R").once("down", () => this.triggerRestart());
    this.input.keyboard?.addKey("ENTER").once("down", () => this.triggerRestart());
    this.input.keyboard?.addKey("SPACE").once("down", () => this.triggerRestart());

    // Mirror the overlay's PLAY AGAIN button so the user can press
    // either the canvas button or the overlay button.
    const onRestartEvent = (): void => {
      window.removeEventListener("traszka:restart", onRestartEvent);
      this.triggerRestart();
    };
    window.addEventListener("traszka:restart", onRestartEvent);

    // Suppress lint complaint about unused hitW/hitH (kept for future).
    void hitW; void hitH;
  }

  private triggerRestart(): void {
    if (this.restartTriggered) return;
    this.restartTriggered = true;
    clearPlayerIdent();
    const root = document.getElementById("overlay-root");
    void showInfoPrompt(root);
    this.scene.start("StartScene");
  }
}